import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import MainLayout from "@/layouts/MainLayout";
import { Game } from "@/types/game";
import { Helmet } from "react-helmet-async";
import { MaterialIcon } from "@/components/ui/material-icon";
import { Loader2 } from "lucide-react";
import AnimatedSection from "@/components/AnimatedSection";
import ParallaxHero from "@/components/ParallaxHero";
import FeaturedGamesGrid from "@/components/FeaturedGamesGrid";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getPrimaryGameImage } from "@/lib/gameImages";

const Games = () => {
  const [complexityFilter, setComplexityFilter] = useState<string>("all");
  const [playerFilter, setPlayerFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("default");
  const { settings } = useSiteSettings();
  const gamesColumns = settings.games_page_columns === "2" ? 2 : settings.games_page_columns === "4" ? 4 : 3;

  const { data: games, isLoading } = useQuery({
    queryKey: ["published-games"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("games")
        .select("*, gallery_images:game_gallery_images(*)")
        .eq("is_published", true)
        .order("sort_order");

      if (error) throw error;
      return data as unknown as Game[];
    },
  });

  const { data: heroSetting } = useQuery({
    queryKey: ["site-setting", "games_hero_image"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("setting_value")
        .eq("setting_key", "games_hero_image")
        .maybeSingle();
      if (error) throw error;
      return data?.setting_value;
    },
  });

  // Complexity filter options in fixed order: Light, Medium, Heavy
  const complexityOptions = useMemo(() => {
    if (!games) return [];
    const values = games.map((g) => g.complexity).filter(Boolean) as string[];
    const order = { light: 1, medium: 2, heavy: 3 };
    const unique = [...new Set(values.map((v) => v.toLowerCase()))];
    const ordered = unique.sort(
      (a, b) => (order[a as keyof typeof order] ?? 99) - (order[b as keyof typeof order] ?? 99),
    );
    return ordered.map((v) => v.charAt(0).toUpperCase() + v.slice(1));
  }, [games]);

  // Fixed player count options
  const playerOptions = ["2", "3", "4", "5", "6", "8", "10+"];

  // Parse player range from game's number_of_players field (e.g., "2-8" or "3-6")
  const gameSupportsPlayerCount = (game: Game, filterValue: string): boolean => {
    if (!game.number_of_players) return false;

    const playerStr = game.number_of_players.toLowerCase().replace(/\s+/g, "");

    // Handle "10+" filter - game supports 10+ if max is >= 10
    if (filterValue === "10+") {
      const rangeMatch = playerStr.match(/(\d+)-(\d+)/);
      if (rangeMatch) {
        const max = parseInt(rangeMatch[2], 10);
        return max >= 10;
      }
      // Check for single number >= 10
      const singleMatch = playerStr.match(/^(\d+)\+?$/);
      if (singleMatch) {
        return parseInt(singleMatch[1], 10) >= 10;
      }
      return false;
    }

    const targetCount = parseInt(filterValue, 10);

    // Handle range format like "2-8"
    const rangeMatch = playerStr.match(/(\d+)-(\d+)/);
    if (rangeMatch) {
      const min = parseInt(rangeMatch[1], 10);
      const max = parseInt(rangeMatch[2], 10);
      return targetCount >= min && targetCount <= max;
    }

    // Handle single number like "4" or "4+"
    const singleMatch = playerStr.match(/^(\d+)\+?$/);
    if (singleMatch) {
      const num = parseInt(singleMatch[1], 10);
      if (playerStr.includes("+")) {
        return targetCount >= num;
      }
      return targetCount === num;
    }

    return false;
  };

  // Filter and sort games
  const filteredGames = useMemo(() => {
    if (!games) return [];
    let result = games.filter((game) => {
      const matchesComplexity =
        complexityFilter === "all" || game.complexity?.toLowerCase() === complexityFilter.toLowerCase();
      const matchesPlayers = playerFilter === "all" || gameSupportsPlayerCount(game, playerFilter);
      return matchesComplexity && matchesPlayers;
    });

    // Sort games
    if (sortBy === "title") {
      result = [...result].sort((a, b) => a.title.localeCompare(b.title));
    } else if (sortBy === "complexity") {
      const complexityOrder: Record<string, number> = { light: 1, medium: 2, heavy: 3 };
      result = [...result].sort((a, b) => {
        const aOrder = complexityOrder[a.complexity?.toLowerCase() || ""] || 99;
        const bOrder = complexityOrder[b.complexity?.toLowerCase() || ""] || 99;
        return aOrder - bOrder;
      });
    }

    return result;
  }, [games, complexityFilter, playerFilter, sortBy]);

  const getComplexityIcon = (complexity: string | null) => {
    if (!complexity) return "layers";
    const lower = complexity.toLowerCase();
    if (lower === "light") return "stat_0";
    if (lower === "medium") return "layers";
    return "stacks";
  };

  // Use custom hero image, or fall back to first game's image
  const heroImage = heroSetting || games?.[0]?.hero_image_url || (games?.[0] ? getPrimaryGameImage(games[0]) : null);

  return (
    <MainLayout>
      <Helmet>
        <title>Our Games | Rogue Skull Games</title>
        <meta
          name="description"
          content="Explore our collection of tabletop games. From battle royale card games to strategic adventures."
        />
        <link rel="canonical" href="https://rogueskullgames.com/games" />
        <meta property="og:title" content="Our Games | Rogue Skull Games" />
        <meta
          property="og:description"
          content="Our full catalog of tabletop games — card games, strategy titles, and more."
        />
        <meta property="og:url" content="https://rogueskullgames.com/games" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero Section with Parallax */}
      <ParallaxHero
        imageUrl={heroImage}
        alt="Games hero"
        className="landing-page-hero"
        heightClass="min-h-[620px] md:min-h-[600px]"
      >
        {/* Content */}
        <div className="container hero-content z-20">
          <div className="">
            <div className="">
              <span className="inline-block text-blood-red font-semibold tracking-[0.3em] mb-2 animate-slide-in">
                OUR GAMES
              </span>
              <h1
                className="text-4xl md:text-5xl lg:text-6xl text-dark-foreground font-display font-bold leading-[1.1] mb-6 animate-fade-up"
                style={{ animationDelay: "0.1s" }}
              >
                Epic Tabletop Adventures
              </h1>
              <p
                className="text-dark-foreground/80 text-lg md:text-xl leading-relaxed max-w-2xl animate-fade-up"
                style={{ animationDelay: "0.2s" }}
              >
                Explore our collection of tabletop games designed to bring excitement, strategy, and unforgettable
                moments to your game nights.
              </p>
            </div>
          </div>
        </div>
      </ParallaxHero>

      {/* Filter Section */}
      {games && games.length > 0 && (
        <section className="section-dark py-8 border-b border-t border-border">
          <div className="container mx-auto">
            <div className="flex flex-wrap items-center gap-4 md:gap-6">
              <span className="text-muted-foreground text-sm uppercase tracking-wider">Filter by:</span>

              {complexityOptions.length > 0 && (
                <Select value={complexityFilter} onValueChange={setComplexityFilter}>
                  <SelectTrigger className="w-[160px] bg-card border-border text-card-foreground">
                    <SelectValue placeholder="Complexity" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Complexity</SelectItem>
                    {complexityOptions.map((option) => (
                      <SelectItem key={option} value={option} className="capitalize">
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <Select value={playerFilter} onValueChange={setPlayerFilter}>
                <SelectTrigger className="w-[160px] bg-card border-border text-card-foreground">
                  <SelectValue placeholder="Players" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Players</SelectItem>
                  {playerOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option} Players
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <span className="text-muted-foreground text-sm uppercase tracking-wider" aria-live="polite">
                {filteredGames.length} {filteredGames.length === 1 ? "Product" : "Products"}
              </span>

              <div className="flex items-center gap-4 md:ml-auto">
                <span className="text-muted-foreground text-sm uppercase tracking-wider">Sort by:</span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[140px] bg-card border-border text-card-foreground">
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">Default</SelectItem>
                    <SelectItem value="title">Title</SelectItem>
                    <SelectItem value="complexity">Complexity</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(complexityFilter !== "all" || playerFilter !== "all" || sortBy !== "default") && (
                <button
                  onClick={() => {
                    setComplexityFilter("all");
                    setPlayerFilter("all");
                    setSortBy("default");
                  }}
                  className="text-blood-red text-sm hover:underline"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Games Section */}
      <div className="flex flex-col section-dark">
        {isLoading ? (
          <section className="section-dark py-16">
            <div className="container mx-auto flex justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          </section>
        ) : !games || games.length === 0 ? (
          <section className="section-dark py-16">
            <div className="container mx-auto text-center">
              <p className="text-dark-foreground/60">No games available yet. Check back soon!</p>
            </div>
          </section>
        ) : filteredGames.length === 0 ? (
          <section className="section-dark py-16">
            <div className="container mx-auto text-center">
              <p className="text-dark-foreground/60">No games match your filters.</p>
              <button
                onClick={() => {
                  setComplexityFilter("all");
                  setPlayerFilter("all");
                }}
                className="mt-4 text-blood-red hover:underline"
              >
                Clear filters
              </button>
            </div>
          </section>
        ) : (
          <FeaturedGamesGrid
            games={filteredGames}
            forceColumns={gamesColumns}
            showAddToBag={true}
            staggered={false}
            paddingTopClass="pt-16"
          />
        )}
      </div>

      {/* Development Progress CTA Section */}
      {games && games.length > 0 && (
        <section className="section-dark py-16 border-t border-b border-dark-foreground/10">
          <div className="container mx-auto">
            <AnimatedSection animation="fade-up">
              <div className="max-w-3xl mx-auto text-center">
                <MaterialIcon name="trending_up" size="xl" className="text-blood-red mb-6" />
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold text-dark-foreground mb-4">
                  Curious About What's Next?
                </h2>
                <p className="text-dark-foreground/70 text-lg leading-relaxed mb-8">
                  Track the progress of our games in development. See what stage each project is at and get a glimpse of
                  what's coming to your tabletop.
                </p>
                <Link to="/about#development-progress" className="btn-outline-light inline-flex items-center gap-2">
                  <span>View Development Progress</span>
                  <MaterialIcon name="arrow_forward" size="sm" />
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </section>
      )}
    </MainLayout>
  );
};

export default Games;
